import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { Alert, Typography } from "@mui/material"
import { ScheduleTopmost } from "routes/schedule/scheduleIntroduction"
import { ErrorButton } from "lib/@components/colored-buttons/error-button"
import { useReferenceState } from "lib/@hooks/use-reference"
import { HighlightAudit, ScheduleIntroEdited, SummaryDataChanged } from "event-definitions"
import { getAuditsByReferenceIdAndType } from "routes/audit/controller/get-audits-by-reference-id"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { useEffect, useMemo, useState } from "react"
import { updateAudit } from "lib/@offline/queued-audits"
import { If } from "lib/@components/switch"
import { SHORT_DESCRIPTION_DRAFT_REJECTED } from "routes/audit/lib/audit-constants"

export function Rejection() {
    const { schedule } = useBoundContext()
    return (
        !!schedule.rejectionReason && (
            <ScheduleTopmost.Plug>
                <RejectionInner />
            </ScheduleTopmost.Plug>
        )
    )
}

function RejectionInner() {
    const { schedule, referenceId } = useBoundContext()
    const [, setTab] = useReferenceState("ScheduleRightBarTab", "")
    const [currentTab] = useSearchParam("ScheduleRightBarTab")
    const [changed, setChanged] = useState(false)
    const audits = getAuditsByReferenceIdAndType.useResults(referenceId, "customSchedule", {
        shortDescription: SHORT_DESCRIPTION_DRAFT_REJECTED,
    })

    const rejectionAudit = useMemo(() => (audits && audits.length > 0 ? audits[0] : null), [audits])
    const rejectionId = rejectionAudit?.id ?? null
    const isDismissed = rejectionAudit?.payload?.dismissed ?? false

    ScheduleIntroEdited.useEvent(() => {
        if (changed) return
        setChanged(true)
    })

    SummaryDataChanged.useEvent(() => {
        if (changed) return
        setChanged(true)
    })

    useEffect(() => {
        if (!changed) return
        if (!rejectionId) return
        if (isDismissed) return

        updateAudit({
            auditId: rejectionId,
            type: "customSchedule",
            referenceId,
            payload: {
                ...rejectionAudit?.payload,
                dismissed: true,
            },
        })
    }, [changed, rejectionId, isDismissed, rejectionAudit, referenceId])

    return (
        <If truthy={!!schedule.rejectionReason && !isDismissed}>
            <Alert
                data-cy="rejection-alert"
                severity="error"
                sx={{ my: 2 }}
                action={
                    <ErrorButton
                        data-cy="view-rejection-button"
                        text="View Rejection"
                        sx={{ alignSelf: "center", mr: 1, mb: 0.5 }}
                        onClick={handleClick}
                    />
                }
            >
                <Typography variant="subtitle1" component="div" gutterBottom>
                    Draft Rejected
                </Typography>
                <Typography variant="body1">
                    The draft was rejected. The rejector's explanation can be viewed in the Justifications tab.
                </Typography>
            </Alert>
        </If>
    )

    function handleClick() {
        if (currentTab !== "justifications") setTab("justifications")
        if (rejectionId) {
            HighlightAudit.raise(rejectionId)
        }
    }
}
